import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';
import { STORAGE_KEY } from '@/types/persistence';
import {
  setLockStore,
  enableSaves,
  disableSaves,
  rehydrateFromLock,
} from '@/persistence/lockStorageBridge';

const DEFERRED_KEY = 'app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys() {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  try {
    const stored = localStorage.getItem(DEFERRED_KEY);
    if (stored === 'skipped') return 'skipped';
    if (stored === 'enabled') return 'enabled';
  } catch {
    // localStorage may be unavailable
  }
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'encrypted',
    encryptedKeys: [STORAGE_KEY],
    appRootSelector: '#root',
    lockOnHide: false,
    idleTimeout: 900,
    ui: false,
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false,
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  const skipEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'skipped');
    } catch {
      // ignore
    }
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setLockStore(null);
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
    enableSaves();
  }, []);

  const enableEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'enabled');
    } catch {
      // ignore
    }
    clearAppLockMetaKeys();
    disableSaves();
    setState((prev) => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision === 'skipped') {
      setLockStore(null);
      enableSaves();
      return;
    }

    if (state.decision !== 'enabled') return;

    disableSaves();
    const instance = createLock();
    instanceRef.current = instance;

    const onLock = () => {
      setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState((prev) => ({
        ...prev,
        isLocked: false,
        isUnlocked: true,
        isConfigured: true,
      }));
      setLockStore(instance.store);
      void rehydrateFromLock(instance.store).catch((err) => {
        console.warn('rehydrateFromLock failed', err);
      });
    };
    const onPasswordClear = () => {
      setState((prev) => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState((prev) => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    setState((prev) => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked:
        instance.state === 'unlocked' || instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    if (instance.state === 'unlocked') {
      setLockStore(instance.store);
      void rehydrateFromLock(instance.store).catch((err) => {
        console.warn('rehydrateFromLock failed', err);
      });
    }

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  if (state.decision === 'enabled' && !state.lock) return null;

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
